import React, { FunctionComponent, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { PageLayout } from '../../common/PageLayout';
import { useSelectWithParams } from '../../../../utils/selector.utils';
import { IMeetingSimple } from '../../../../app/resource/meeting-simple/meeting-simple.types';
import { UserAvatar } from '../../common/UserAvatar';
import { IUser } from '../../../../app/resource/user/user.types';
import { IMeetingReportsPageProps } from './types';
import Topbar from '../Topbar';
import { fetchMeetingReports } from '../../../../app/page/page-storage/reports/reports.actions';
import { reportPageSelectors } from '../../../../app/page/page-storage/reports/reports.selectors';
import { userSelectors } from '../../../../app/resource/user/user.selectors';
import { meetingSimpleSelectors } from '../../../../app/resource/meeting-simple/meeting-simple.selectors';
import KpisMetrics from '../KpisMetrics';
import QuarterlyInitiatives from '../QuarterlyInitiative';
import MeetingMemberDoughnutMetricCards from './MeetingMemberDoughnutMetricCards';
import { fetchUsers } from '../../../../app/page/page-storage/users/users.actions';
import { fetchMeetingsSimple } from '../../../../app/page/page-storage/meetings-simple/meetings-simple.actions';

const MeetingMemberReportsPage: FunctionComponent<IMeetingReportsPageProps> = () => {
  const dispatch = useDispatch();
  const { meetingId, memberId } = useParams<{ meetingId: IMeetingSimple['id']; memberId: IUser['id'] }>();
  const member = useSelectWithParams(userSelectors.selectResourceById, memberId);
  const meeting = useSelectWithParams(meetingSimpleSelectors.selectResourceById, meetingId);

  useEffect(() => {
    const today = new Date();
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1);

    dispatch(fetchUsers());
    dispatch(fetchMeetingsSimple());

    if (memberId) {
      dispatch(fetchMeetingReports(meetingId, { dateSince: firstDayOfYear, dateUntil: today, memberId }));
    }
  }, [dispatch, meetingId, memberId]);

  const reports = useSelectWithParams(reportPageSelectors.selectMeetingReportByMeetingId, meetingId);
  if (!reports) {
    return null;
  }

  const report = reports.byMember?.[memberId];

  return (
    <PageLayout smallSidebar>
      <div className="row mb-2">
        <div className="col-12">
          <div className="panel">
            <div className="panel-header small-header no-border pb-0 mb-1">
              <h2>
                Meeting Reports - <span className="bold">{meeting && meeting.title}</span>
              </h2>
            </div>
            <div className="cards-container-overflow meeting-report">
              <div className="cards-container">
                <div className="panel">
                  <div className="panel-body pt-0 ho-hover px-0">
                    <div className="border-radius-1 lightGray-bg p-2">
                      <div className="row">
                        <div className="col">
                          <Topbar meetingId={meetingId} memberId={memberId} />
                          {report && (
                            <>
                              <div className="panel my-1 p-2">
                                <div className="flex align-center" style={{ height: 120 }}>
                                  {member && (
                                    <>
                                      <UserAvatar userInfo={member} size="100" />
                                      <div className="flex flex-column ml-2" style={{ flex: 1 }}>
                                        <div className="flex align-center justify-between border-bottom py-1">
                                          <div className="text-left bold">{`${member.firstName} ${member.lastName}`}</div>
                                        </div>
                                        <div className="flex align-center justify-between py-1">
                                          <div className="text-left">
                                            <span>{member.jobTitle}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex align-center flex-column">
                                        <div className="avg-score">
                                          {report.attendance.avgScore > 0
                                            ? Math.round(report.attendance.avgScore * 10) / 10
                                            : '-'}
                                        </div>
                                        <span className="ml-1 mt-1">Average Meeting Score</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                              <MeetingMemberDoughnutMetricCards report={report} />
                              <QuarterlyInitiatives goalList={report.goals.list} />
                              <KpisMetrics kpis={report.kpis} />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default MeetingMemberReportsPage;
